import React from 'react';
import { useAuth } from '../contexts/AuthContext';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: Array<'admin' | 'videomaker' | 'video_editor' | 'copywriter' | 'publisher'>;
  fallback?: React.ReactNode;
  adminOnly?: boolean;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallback = null,
  adminOnly = false
}) => {
  const { user } = useAuth();

  if (!user) {
    return <>{fallback}</>;
  }

  // Se adminOnly è true, verifica solo se è admin
  if (adminOnly) {
    return user.role === 'admin' ? <>{children}</> : <>{fallback}</>;
  }

  // Verifica se il ruolo dell'utente è nella lista dei ruoli permessi.
  // Publisher è aggiuntivo: chi lo ha in più passa anche i controlli su 'publisher'
  const hasPermission =
    allowedRoles.includes(user.role) ||
    (allowedRoles.includes('publisher') && user.is_publisher === true);

  return hasPermission ? <>{children}</> : <>{fallback}</>;
};

// Hook per verificare i permessi
export const useRoleCheck = () => {
  const { user } = useAuth();
  
  const hasRole = (requiredRoles: Array<'admin' | 'videomaker' | 'video_editor' | 'copywriter' | 'publisher'>) => {
    if (!user) return false;
    if (requiredRoles.includes('publisher') && user.is_publisher === true) return true;
    return requiredRoles.includes(user.role);
  };
  
  const isAdmin = () => user?.role === 'admin';
  const isVideomaker = () => user?.role === 'videomaker';
  const isVideoEditor = () => user?.role === 'video_editor';
  const isCopywriter = () => user?.role === 'copywriter';
  // Publisher è un ruolo aggiuntivo: vale sia come ruolo principale
  // sia sommato a videomaker o video editor
  const isPublisher = () => user?.role === 'publisher' || user?.is_publisher === true;
  // Chi vede la pagina Clienti con tutti i clienti e i loro dati
  const canSeeAllClients = () => isAdmin() || isPublisher();

  return {
    user,
    hasRole,
    isAdmin,
    isVideomaker,
    isVideoEditor,
    isCopywriter,
    isPublisher,
    canSeeAllClients
  };
};

// Componente per mostrare un messaggio di accesso negato
export const AccessDenied: React.FC<{
  message?: string;
  showRole?: boolean;
}> = ({ 
  message = "Non hai i permessi necessari per accedere a questa funzionalità.",
  showRole = false
}) => {
  const { user } = useAuth();

  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center space-y-4 max-w-md mx-auto p-8">
        <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">Accesso Negato</h3>
        <p className="text-gray-600">{message}</p>
        {showRole && user && (
          <p className="text-sm text-gray-500">
            Il tuo ruolo attuale: <span className="font-medium">{user.role}</span>
          </p>
        )}
        <div className="pt-4">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Torna Indietro
          </button>
        </div>
      </div>
    </div>
  );
};